import { useEffect, useState } from 'react';

/**
 * W10 — guest "shortlist" / "my picks" stored in localStorage per wine list.
 *
 * Frontend-only: no backend, no auth. Each wine list slug gets its own
 * `shortlist:<slug>` key storing an array of WineListItem ids the guest
 * has hearted on the public list. Survives page refresh; doesn't sync
 * across devices; harmless if cleared by the browser.
 */

const KEY = (slug: string) => `shortlist:${slug}`;

const safeParseIds = (raw: string | null): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Filter to plain strings + dedupe defensively in case the storage was
    // hand-edited or shared with an older version of this code.
    const strings = parsed.filter((x): x is string => typeof x === 'string');
    return Array.from(new Set(strings));
  } catch {
    return [];
  }
};

/** Read the shortlist for a slug. Returns [] in SSR or on parse failure. */
export const getShortlist = (slug: string): string[] => {
  if (typeof window === 'undefined') return [];
  return safeParseIds(window.localStorage.getItem(KEY(slug)));
};

/** Overwrite the stored shortlist. Deduplicated, safely no-ops on quota errors. */
export const setShortlist = (slug: string, ids: string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    const unique = Array.from(new Set(ids));
    window.localStorage.setItem(KEY(slug), JSON.stringify(unique));
  } catch {
    // QuotaExceeded / DOM exception when localStorage is disabled — silent.
  }
};

/** Add or remove an item from the shortlist; returns the new array. */
export const toggleShortlistItem = (slug: string, itemId: string): string[] => {
  const current = getShortlist(slug);
  const next = current.includes(itemId)
    ? current.filter(id => id !== itemId)
    : [...current, itemId];
  setShortlist(slug, next);
  return next;
};

export const isShortlisted = (slug: string, itemId: string): boolean =>
  getShortlist(slug).includes(itemId);

/** Remove the entire shortlist for a slug. */
export const clearShortlist = (slug: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY(slug));
  } catch {
    // noop
  }
};

/**
 * React hook: subscribes to the shortlist for a slug. State stays in sync
 * with localStorage across:
 *   - in-page mutations via `toggle` / `clear`
 *   - cross-tab updates via the native `storage` event
 *
 * Returns a stable API object; `ids` is the read-only current array.
 */
export const useShortlist = (slug: string | undefined): {
  ids: string[];
  count: number;
  toggle: (itemId: string) => void;
  clear: () => void;
  has: (itemId: string) => boolean;
} => {
  const [ids, setIds] = useState<string[]>(() => (slug ? getShortlist(slug) : []));

  useEffect(() => {
    if (!slug) { setIds([]); return; }
    setIds(getShortlist(slug));

    // Cross-tab sync: another tab editing the same slug fires a storage
    // event that we observe and reconcile.
    const handler = (e: StorageEvent) => {
      if (e.key === KEY(slug)) {
        setIds(safeParseIds(e.newValue));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [slug]);

  return {
    ids,
    count: ids.length,
    toggle: (itemId: string) => {
      if (!slug) return;
      const next = toggleShortlistItem(slug, itemId);
      setIds(next);
    },
    clear: () => {
      if (!slug) return;
      clearShortlist(slug);
      setIds([]);
    },
    has: (itemId: string) => ids.includes(itemId),
  };
};
